import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  IconButton,
  Stack,
  InputAdornment,
  Tooltip,
  ButtonGroup,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useIntl } from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';
import { MuiChipsInput } from 'mui-chips-input';
import { useFormContext } from 'react-hook-form';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import FilePaper from './FilePaper';
import validateEmail from '../../../Helpers/Validation/validateEmail';
import { emailValidationString } from '../Utils/constants';
import ContentBodyBox from '../../../Components/ContentBodyBox';

const useStyles = () => ({
  filesRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '10vh',
  },
  chipInputs: {
    '& .MuiInputBase-root': {
      paddingRight: 0,
      paddingLeft: 0,
      marginBottom: '2vh',
    },
    '& .MuiChipsInput-Chip': {
      size: 'small',
    },
  },
});

const selectExtIcon = (fileName) => {
  const arrayFromName = fileName.split('.'); //creates an array filled by elements before and after the . char [filename, extension]
  const extension = arrayFromName.pop(); //removes the first element of array (in this case filename)

  let icon = <InsertDriveFileRoundedIcon sx={{ color: '#579ff3' }} />;

  if (extension === 'jpg' || extension === 'png') {
    icon = <ImageRoundedIcon sx={{ color: '#ffb2e1' }} />;
  } else if (extension === 'pdf') {
    icon = <PictureAsPdfRoundedIcon sx={{ color: 'rgba(205, 0, 0, 0.6)' }} />;
  }

  return icon;
};

function EmailChip(Chip, key, props) {
  return <Chip key={key} size="small" variant="outlined" {...props} />;
}

const EmailWidget = function EmailWidget({
  defaultEmail = null,
  replyData = null,
}) {
  const classes = useStyles();
  const intl = useIntl();
  const [files, setFiles] = useState([]);
  const [cc, setCc] = useState(null);
  const [bcc, setBcc] = useState(null);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [subjectValue, setSubjectValue] = useState(replyData?.subject || '');
  const [replyAll, setReplyAll] = useState(false);
  const [showBody, setShowBody] = useState(false);

  const {
    register, //sends input data
    setValue,
    reset,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    reset({
      subject: replyData?.subject || '',
      body: '',
    });
  }, []);

  const handleChangeFiles = (e) => {
    const filesArray = [...e.target.files];
    setFiles(filesArray);
    setValue('files', filesArray);
  };
  const handleRemoveFile = (fileName) => {
    const copyFiles = [...files];
    const index = files.findIndex(({ name }) => name === fileName);
    copyFiles.splice(index, 1); //remove one element on index

    setFiles(copyFiles);
    setValue('files', copyFiles);
  };

  const handleChangeSubjectValue = (e) => {
    setSubjectValue(e.target.value);
    setValue('subject', e.target.value);
  };

  const handleToggleShowBody = () => {
    setShowBody(!showBody);
  };

  const handleChangeReplyAll = (e) => {
    setReplyAll(e.target.checked);
  };
  useEffect(() => {
    setValue('replyAll', replyAll);
  }, [replyAll]);

  const addReplyTag = (subject) => {
    if (subject.startsWith('Re:')) {
      return subject;
    }
    return `Re: ${subject}`;
  };
  return (
    <div style={{ width: '100%' }}>
      <TextField
        defaultValue={defaultEmail || ''}
        {...register('receiver', {
          required: { value: true, message: intl.messages.MANDATORY_FIELD },
          pattern: {
            value: emailValidationString,
            message: intl.messages.INVALID_EMAIL_ADDRESS,
          },
        })}
        helperText={errors?.receiver?.message || null}
        error={errors?.receiver}
        sx={{ mb: '2vh' }}
        size="medium"
        label={intl.messages.RECEIVER}
        fullWidth
        variant="standard"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {!replyData ? (
                <ButtonGroup
                  aria-label={intl.messages.RECIPIENTS_TYPE}
                  variant="text"
                  size="small"
                >
                  <Tooltip
                    title={`${
                      cc ? intl.messages.DO_NOT_ADD : intl.messages.ADD
                    } ${intl.messages.RECEIVERS} in Cc`}
                    placement="top"
                  >
                    <Button
                      onClick={() => setCc(!cc)}
                      sx={{
                        textTransform: 'none',
                      }}
                    >
                      Cc
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={`${
                      cc ? intl.messages.DO_NOT_ADD : intl.messages.ADD
                    } ${intl.messages.RECEIVERS} in Bcc`}
                    placement="top"
                  >
                    <Button
                      onClick={() => setBcc(!bcc)}
                      sx={{
                        textTransform: 'none',
                      }}
                    >
                      Bcc
                    </Button>
                  </Tooltip>
                </ButtonGroup>
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={replyAll}
                      onChange={handleChangeReplyAll}
                    />
                  }
                  label={intl.messages.REPLY_TO_ALL}
                />
              )}
            </InputAdornment>
          ),
        }}
      />
      {cc && !replyData && (
        <MuiChipsInput
          fullWidth
          renderChip={EmailChip}
          style={classes.chipInputs}
          label={`${intl.messages.RECEIVERS} cc`}
          value={ccEmails}
          onChange={(newEmail) => {
            if (validateEmail(newEmail)) {
              setCcEmails(newEmail);
              setValue('cc', newEmail);
            }
          }}
          variant="standard"
          placeholder={intl.messages.ADD_RECEIVER}
        />
      )}
      {bcc && !replyData && (
        <MuiChipsInput
          fullWidth
          renderChip={EmailChip}
          style={classes.chipInputs}
          label={`${intl.messages.RECEIVERS} bcc`}
          value={bccEmails}
          onChange={(newEmail) => {
            if (validateEmail(newEmail)) {
              setBccEmails(newEmail);
              setValue('bcc', newEmail);
            }
          }}
          variant="standard"
          placeholder={intl.messages.ADD_RECEIVER}
        />
      )}
      <TextField
        value={
          replyData?.subject ? addReplyTag(replyData?.subject) : subjectValue
        }
        onChange={handleChangeSubjectValue}
        disabled={replyData?.subject}
        sx={{ mb: '2vh' }}
        size="medium"
        id="description-area"
        label={intl.messages.SUBJECT}
        fullWidth
        variant="standard"
      />
      <TextField
        {...register('body')}
        placeholder={intl.messages.EMAILBODY}
        size="medium"
        id="description-area"
        multiline
        fullWidth
        rows={10}
        variant="standard"
      />
      {replyData?.body && (
        <Tooltip title={intl.messages.SHOW_CONTENTBODY} placement="bottom">
          <IconButton onClick={() => handleToggleShowBody()}>
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      )}
      {replyData?.body && (
        <ContentBodyBox
          visibility={showBody}
          content={replyData.body}
          transition
          styles={{
            box: {
              backgroundColor: 'transparent',
            },
            content: {
              fontFamily: 'Roboto',
            },
          }}
        />
      )}
      <div style={classes.filesRow}>
        <Stack
          spacing={2}
          direction="row"
          sx={{ alignItems: 'center', overflowX: 'auto', padding: '16px' }}
        >
          {files.map((file) => (
            <FilePaper
              onClickIcon={<CloseIcon />}
              key={file.name}
              fileName={file.name}
              extensionIcon={selectExtIcon(file.name)}
              onClick={handleRemoveFile}
            />
          ))}
        </Stack>
        <Tooltip title={intl.messages.ADD_ATTACHMENTS} placement="left">
          <IconButton>
            <label
              htmlFor="input-file"
              style={{ fontSize: 'xx-small', alignSelf: 'flex-end' }}
            >
              <AttachFileIcon />
              <input
                name="files"
                {...register('files')}
                id="input-file"
                type="file"
                hidden
                multiple
                onChange={handleChangeFiles}
              />
            </label>
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

EmailWidget.propTypes = {
  defaultEmail: PropTypes.string,
  replyData: PropTypes.object,
};

export default EmailWidget;
